import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../contexts/ApiContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye, 
  Mail, 
  Phone, 
  MapPin,
  Users,
  Music,
  ExternalLink,
  Calendar,
  User
} from 'lucide-react';

const AdminBandApplications = () => {
  const { api } = useApi();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'pending',
    search: '',
    page: 1
  });
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    page: 1,
    limit: 10
  });

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        status: filters.status,
        page: filters.page.toString(),
        limit: '10'
      });
      
      if (filters.search) {
        params.append('search', filters.search);
      }

      const response = await api.get(`/band-applications/admin?${params}`);
      setApplications(response.data.data.applications);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleApprove = async (applicationId) => {
    try {
      setActionLoading(true);
      await api.put(`/band-applications/admin/${applicationId}/approve`);
      await fetchApplications();
      setShowModal(false);
      setSelectedApp(null);
    } catch (error) {
      console.error('Failed to approve application:', error);
      alert('Failed to approve application. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (applicationId, reason) => {
    try {
      setActionLoading(true);
      await api.put(`/band-applications/admin/${applicationId}/reject`, { reason });
      await fetchApplications();
      setShowModal(false);
      setSelectedApp(null);
    } catch (error) {
      console.error('Failed to reject application:', error);
      alert('Failed to reject application. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300',
      under_review: 'bg-blue-100 text-blue-800 border-blue-300'
    };

    const icons = {
      pending: <Clock className="w-4 h-4" />,
      approved: <CheckCircle className="w-4 h-4" />,
      rejected: <XCircle className="w-4 h-4" />,
      under_review: <Eye className="w-4 h-4" />
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${styles[status]}`}>
        {icons[status]}
        <span className="ml-1 capitalize">{status.replace('_', ' ')}</span>
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const ApplicationModal = ({ app, onClose, onApprove, onReject }) => {
    const [rejectReason, setRejectReason] = useState('');
    const [showRejectForm, setShowRejectForm] = useState(false);

    if (!app) return null;

    const bandTypesList = app.band_types ? Object.entries(app.band_types)
      .filter(([_, selected]) => selected)
      .map(([type, _]) => type.charAt(0).toUpperCase() + type.slice(1))
      .join(', ') : 'Not specified';

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{app.school_name} Band</h2>
                <p className="text-gray-600">Application #{app.id.slice(0, 8)}</p>
                <div className="mt-2">{getStatusBadge(app.status)}</div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Band Director Information */}
              <div className="bg-midnight text-white p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-3 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Band Director
                </h3>
                <div className="space-y-2">
                  <p><strong>Name:</strong> {app.director_name}</p>
                  <p className="flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    {app.director_email}
                  </p>
                  {app.director_phone && (
                    <p className="flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      {app.director_phone}
                    </p>
                  )}
                </div>
              </div>

              {/* School Information */}
              <div className="bg-midnight text-white p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-3 flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  School Information
                </h3>
                <div className="space-y-2">
                  <p><strong>School:</strong> {app.school_name}</p>
                  {app.school_address && (
                    <p><strong>Address:</strong> {app.school_address}</p>
                  )}
                  {app.school_city && app.school_state && (
                    <p><strong>Location:</strong> {app.school_city}, {app.school_state} {app.school_zip}</p>
                  )}
                  {app.school_phone && (
                    <p className="flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      {app.school_phone}
                    </p>
                  )}
                  {app.school_website && (
                    <p className="flex items-center">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      <a href={app.school_website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {app.school_website}
                      </a>
                    </p>
                  )}
                </div>
              </div>

              {/* Band Information */}
              <div className="bg-midnight text-white p-4 rounded-lg lg:col-span-2">
                <h3 className="font-semibold text-lg mb-3 flex items-center">
                  <Music className="w-5 h-5 mr-2" />
                  Band Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p><strong>Band Types:</strong> {bandTypesList}</p>
                    {app.band_size && <p><strong>Band Size:</strong> {app.band_size}</p>}
                  </div>
                  {app.school_colors && (
                    <div>
                      <p><strong>School Colors:</strong></p>
                      <div className="flex space-x-2 mt-1">
                        {app.school_colors.map((color, index) => (
                          <div
                            key={index}
                            className="w-8 h-8 rounded border-2 border-gray-300"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Submission Details */}
              <div className="bg-midnight text-white p-4 rounded-lg lg:col-span-2">
                <h3 className="font-semibold text-lg mb-3 flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Submission Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p><strong>Submitted:</strong> {formatDate(app.submitted_at)}</p>
                    {app.reviewed_at && (
                      <p><strong>Reviewed:</strong> {formatDate(app.reviewed_at)}</p>
                    )}
                  </div>
                  <div>
                    <p><strong>Terms Agreed:</strong> {app.agreed_to_terms ? '✅ Yes' : '❌ No'}</p>
                    <p><strong>Commission Agreed:</strong> {app.agreed_to_commission ? '✅ Yes' : '❌ No'}</p>
                    <p><strong>Guidelines Agreed:</strong> {app.agreed_to_guidelines ? '✅ Yes' : '❌ No'}</p>
                  </div>
                </div>
                {app.rejection_reason && (
                  <div className="mt-4 p-3 bg-red-900 border border-red-700 rounded text-white">
                    <p><strong>Rejection Reason:</strong> {app.rejection_reason}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {app.status === 'pending' && (
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
                {showRejectForm ? (
                  <div className="flex-1">
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Please provide a reason for rejection..."
                      className="w-full p-3 border border-gray-300 rounded-lg mb-3 text-gray-900 bg-white"
                      rows="3"
                    />
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setShowRejectForm(false)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => onReject(app.id, rejectReason)}
                        disabled={!rejectReason.trim() || actionLoading}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setShowRejectForm(true)}
                      className="px-6 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => onApprove(app.id)}
                      disabled={actionLoading}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {actionLoading ? 'Approving...' : 'Approve'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Band Applications</h1>
        <p className="text-gray-600">Review and approve band signup applications</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border-2 border-black mb-6">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="flex-1 max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
              placeholder="Search by school, director, or email..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Applications List */}
      <div className="bg-white rounded-lg border-2 border-black overflow-hidden">
        {applications.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Found</h3>
            <p className="text-gray-600">
              {filters.status === 'pending' 
                ? 'No pending applications to review.' 
                : `No ${filters.status} applications found.`}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    School & Director
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {applications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{app.school_name}</div>
                        <div className="text-sm text-gray-500">{app.director_name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{app.director_email}</div>
                      {app.director_phone && (
                        <div className="text-sm text-gray-500">{app.director_phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(app.submitted_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(app.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedApp(app);
                          setShowModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <ApplicationModal
          app={selectedApp}
          onClose={() => {
            setShowModal(false);
            setSelectedApp(null);
          }}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
    </div>
  );
};

export default AdminBandApplications;
